import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

/**
 * TranslationCacheService
 * 
 * Centralized caching service for all translation files
 * 
 * Features:
 * - Single source of truth for cached translations
 * - Prevents duplicate HTTP requests
 * - Memory-efficient with shareReplay
 * - Language-aware caching
 * - Clear cache on language change
 */
@Injectable({
  providedIn: 'root'
})
export class TranslationCacheService {
  
  private cache = new Map<string, Observable<any>>();
  private loadedKeys = new Set<string>();
  
  /**
   * Get cached translation observable
   */
  get(cacheKey: string): Observable<any> | null {
    return this.cache.get(cacheKey) || null;
  }
  
  /**
   * Set translation observable in cache
   * Uses shareReplay to prevent duplicate HTTP requests
   */
  set(cacheKey: string, translation$: Observable<any>): void {
    const shared$ = translation$.pipe(shareReplay(1));
    this.cache.set(cacheKey, shared$);
    this.loadedKeys.add(cacheKey);
  }
  
  /**
   * Check if translation is already cached
   */
  has(cacheKey: string): boolean {
    return this.cache.has(cacheKey);
  }
  
  /**
   * Get all loaded cache keys
   */
  getLoadedKeys(): string[] {
    return Array.from(this.loadedKeys);
  }
  
  /**
   * Clear entire cache (use when changing language)
   */
  clear(): void {
    this.cache.clear();
    this.loadedKeys.clear();
  }
  
  /**
   * Clear cache for specific language
   */
  clearLanguage(lang: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      if (key.startsWith(`${lang}_`)) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.loadedKeys.delete(key);
    });
  }
  
  /**
   * Get cache statistics
   */
  getStats(): { totalKeys: number; languages: string[] } {
    const languages = new Set<string>();
    
    this.loadedKeys.forEach(key => {
      const lang = key.split('_')[0];
      languages.add(lang);
    });
    
    return {
      totalKeys: this.loadedKeys.size,
      languages: Array.from(languages)
    };
  }
}
